import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { cx } from 'class-variance-authority';
import { useAuth } from '../../auth/AuthProvider';
import { useOrg } from '../../api/hooks/admin.hook';

const adminNavItems = [
  { label: 'Dashboard', to: '/admin' },
  { label: 'Members', to: '/admin/members' },
  { label: 'Invitations', to: '/admin/invites' },
  { label: 'Billing', to: '/admin/billing' },
  { label: 'Keycloak Sync', to: '/admin/keycloak' },
  { label: 'Audit Log', to: '/admin/audit' },
];

const planBadgeColor: Record<string, string> = {
  free: 'bg-neutral-100 text-neutral-600',
  pro: 'bg-blue-100 text-blue-700',
  elite: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

export const AdminLayout = () => {
  const { user } = useAuth();
  const { data: org, isLoading } = useOrg();

  // Only org admins can access admin pages
  const orgRole = (user as Record<string, unknown> | null)?.org_role;
  if (!isLoading && orgRole !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar */}
      <aside className="w-52 shrink-0 border-r border-neutral-200 bg-white flex flex-col">
        <div className="px-4 py-4 border-b border-neutral-100">
          <h2 className="text-sm font-semibold text-neutral-900 truncate">
            {org?.name ?? 'Organization'}
          </h2>
          {org && (
            <span
              className={cx(
                'inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide',
                planBadgeColor[org.plan] ?? planBadgeColor.free,
              )}
            >
              {org.plan}
            </span>
          )}
        </div>

        <nav className="flex-1 py-2 px-2 space-y-0.5">
          {adminNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              className={({ isActive }) =>
                cx(
                  'block px-3 py-2 rounded text-sm transition-colors',
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-neutral-50 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-neutral-400 text-sm">
            Loading...
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
};
